import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Progress } from '@/components/ui/progress';
import { Zap, Mic, Brain, Scissors, Sparkles, Check, Activity, Clock } from 'lucide-react';
import { useT } from '@/lib/i18n';

const STAGES = [
  { key: 'up.stage1', icon: Zap,      color: '#D4FF00', sub: '↗ chunk 1/4 · 1/4 · …' },
  { key: 'up.stage2', icon: Activity, color: '#00E5FF', sub: '~ 44.1 kHz · isolating voice · ducking music' },
  { key: 'up.stage3', icon: Mic,      color: '#FF5F00', sub: 'whisper-1 · 12 lang detected · 98% confidence' },
  { key: 'up.stage4', icon: Brain,    color: '#A855F7', sub: 'detecting laughter spikes, energy peaks, hooks' },
  { key: 'up.stage5', icon: Sparkles, color: '#FF3D7F', sub: 'scoring virality / engagement / retention' },
  { key: 'up.stage6', icon: Scissors, color: '#D4FF00', sub: 'cutting · captioning · auto-reframe 9:16 / 16:9' },
];

// Fake transcription words that "appear" while Whisper is "transcribing"
const FAKE_WORDS = [
  'Wait', '...', 'did', 'he', 'just', 'say', 'that', '?', 'This', 'is',
  'a', 'GAME', 'changer', 'for', 'real', '...', 'I', 'can\'t', 'believe',
  'it', '!', 'Watch', 'closely', '—', 'the', 'moment', 'everything',
  'shifted', '...', 'and', 'BOOM', '💥',
];

const FAKE_HIGHLIGHTS = [
  { label: 'Funny moment', score: 91 },
  { label: 'Emotional peak', score: 87 },
  { label: 'Viral hook', score: 94 },
  { label: 'Action scene', score: 82 },
  { label: 'Key insight', score: 89 },
];

const Waveform = ({ accent = '#D4FF00', bars = 32 }) => {
  return (
    <div className="flex items-end gap-[3px] h-12">
      {Array.from({ length: bars }).map((_, i) => (
        <motion.div
          key={`bar-${i}`}
          className="w-1 rounded-full"
          style={{ background: accent, opacity: 0.85 }}
          animate={{ height: ['18%', '92%', '34%', '70%', '22%'] }}
          transition={{ duration: 0.9 + (i % 5) * 0.07, repeat: Infinity, delay: i * 0.03 }}
        />
      ))}
    </div>
  );
};

const formatDuration = (seconds, lang) => {
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return lang === 'fr' ? `${s}s` : `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return lang === 'fr' ? `${m}min ${rem}s` : `${m}m ${rem}s`;
};

/**
 * ETA estimator: extrapolates remaining time from elapsed × (100-progress) / progress.
 * - `startedAt` lazy-init useState (immutable across renders) instead of useRef (passes react-hooks/refs).
 * - Tick is computed inside an inner async function (passes react-hooks/purity).
 */
const useEta = (progress) => {
  const [startedAt] = useState(() => Date.now());
  const [nowTs, setNowTs] = useState(() => Date.now());
  useEffect(() => {
    let id;
    const tick = () => { setNowTs(Date.now()); };
    id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  const elapsed = Math.max(0, (nowTs - startedAt) / 1000);
  let eta;
  if (progress < 5) eta = null;
  else if (progress >= 100) eta = 0;
  else eta = elapsed * (100 - progress) / progress;
  return { elapsed, eta };
};

const UploadProcessing = ({ progress, stageIndex }) => {
  const { t, lang } = useT();
  const [transcriptIdx, setTranscriptIdx] = useState(0);
  const [highlightIdx, setHighlightIdx] = useState(0);
  const { elapsed, eta } = useEta(progress);

  useEffect(() => {
    const t1 = setInterval(() => setTranscriptIdx((i) => (i + 1) % (FAKE_WORDS.length - 6)), 240);
    const t2 = setInterval(() => setHighlightIdx((i) => (i + 1) % FAKE_HIGHLIGHTS.length), 1200);
    return () => { clearInterval(t1); clearInterval(t2); };
  }, []);

  const safeStage = Math.min(stageIndex ?? 0, STAGES.length - 1);
  const current = STAGES[safeStage];
  const transcriptWindow = FAKE_WORDS.slice(transcriptIdx, transcriptIdx + 6);

  return (
    <div className="space-y-6" data-testid="upload-processing">
      {/* Hero card with live stage */}
      <motion.div
        layout
        className="relative overflow-hidden p-8 rounded-3xl border border-[#D4FF00]/30 bg-gradient-to-br from-[#D4FF00]/8 via-black/50 to-black"
      >
        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full blur-[80px] opacity-50" style={{ background: current.color }} />
        <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full blur-[80px] opacity-30" style={{ background: current.color }} />

        <div className="relative">
          <div className="flex items-center gap-3 mb-2">
            <motion.div
              key={safeStage}
              initial={{ scale: 0.7, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: `${current.color}22`, border: `1px solid ${current.color}55` }}
            >
              <current.icon className="w-5 h-5 animate-pulse" style={{ color: current.color }} />
            </motion.div>
            <span className="text-xs uppercase tracking-[0.25em] font-mono" style={{ color: current.color }}>
              {t('up.stage.label', { n: safeStage + 1, total: STAGES.length }) || `Stage ${safeStage + 1} / ${STAGES.length}`}
            </span>
          </div>
          <AnimatePresence mode="wait">
            <motion.h2
              key={current.key}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="font-heading text-3xl lg:text-4xl font-black tracking-tighter mb-1"
            >
              {t(current.key)}
            </motion.h2>
          </AnimatePresence>
          <p className="text-xs text-zinc-500 font-mono mb-6">{current.sub}</p>

          <Progress value={progress} className="h-2 bg-white/5" data-testid="upload-progress-bar" />
          <div className="flex justify-between items-center mt-2 text-xs text-zinc-500 font-mono">
            <span>{Math.round(progress)}%</span>
            <span data-testid="upload-eta" className="flex items-center gap-1.5">
              <Clock className="w-3 h-3" />
              {eta === null ? (
                lang === 'fr' ? 'estimation…' : 'estimating…'
              ) : eta === 0 ? (
                lang === 'fr' ? 'finalisation…' : 'finalizing…'
              ) : (
                <>
                  <span className="text-zinc-300">{lang === 'fr' ? `il reste ~${formatDuration(eta, lang)}` : `~${formatDuration(eta, lang)} left`}</span>
                  <span className="text-zinc-600">· {formatDuration(elapsed, lang)} {lang === 'fr' ? 'écoulé' : 'elapsed'}</span>
                </>
              )}
            </span>
          </div>
          <p className="text-[10px] text-zinc-600 font-mono mt-1.5">
            {lang === 'fr' ? "Ne ferme pas la page — tu peux laisser tourner en arrière-plan." : "Don't close this page — you can leave it running in the background."}
          </p>

          {/* Reassurance — no fake ETA, just quality-first framing */}
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.15 }}
            className="mt-5 flex items-start gap-3 p-4 rounded-xl border border-white/5 bg-white/[0.02]"
            data-testid="upload-reassurance"
          >
            <motion.div
              animate={{ opacity: [0.4, 1, 0.4] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
              className="w-1.5 h-1.5 rounded-full bg-[#D4FF00] mt-1.5 shrink-0"
            />
            <div className="text-xs text-zinc-300 leading-relaxed">
              {lang === 'fr'
                ? 'Le traitement peut prendre quelques instants. Nous analysons votre vidéo en profondeur afin de vous proposer les meilleurs clips possibles.'
                : 'Processing may take a moment. We analyse your video in depth to deliver the best possible clips.'}
              <span className="block text-zinc-500 mt-1">
                {lang === 'fr'
                  ? 'Une vidéo plus longue peut nécessiter davantage de temps.'
                  : 'A longer video may require more time.'}
              </span>
            </div>
          </motion.div>

          {/* Stage breadcrumbs */}
          <div className="flex flex-wrap gap-2 mt-6">
            {STAGES.map((s, i) => {
              const done = i < safeStage;
              const active = i === safeStage;
              return (
                <div
                  key={s.key}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-mono uppercase tracking-wider border transition ${
                    done ? 'border-[#D4FF00]/30 text-[#D4FF00]/90 bg-[#D4FF00]/5' :
                    active ? 'border-white/30 text-white bg-white/5' :
                    'border-white/5 text-zinc-600 bg-white/[0.02]'
                  }`}
                  data-testid={`stage-pill-${i}`}
                >
                  {done ? <Check className="w-3 h-3" /> : <s.icon className="w-3 h-3" />}
                  <span>{t(s.key)?.split(/[.…]/)[0]?.slice(0, 16) || `Stage ${i+1}`}</span>
                </div>
              );
            })}
          </div>
        </div>
      </motion.div>

      {/* Two side-by-side: waveform + transcript */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="p-5 rounded-2xl border border-white/5 bg-white/[0.02]" data-testid="processing-waveform">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">// Audio energy</span>
            <Activity className="w-4 h-4 text-[#00E5FF]" />
          </div>
          <Waveform accent="#00E5FF" />
          <div className="mt-3 flex gap-2 text-[10px] font-mono">
            <span className="px-2 py-0.5 rounded bg-white/5 text-zinc-300">peaks: 12</span>
            <span className="px-2 py-0.5 rounded bg-white/5 text-zinc-300">rms: -14 dB</span>
            <span className="px-2 py-0.5 rounded bg-white/5 text-zinc-300">vad: 87%</span>
          </div>
        </div>

        <div className="p-5 rounded-2xl border border-white/5 bg-white/[0.02]" data-testid="processing-transcript">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">// Transcript (Whisper)</span>
            <Mic className="w-4 h-4 text-[#FF5F00]" />
          </div>
          <div className="font-mono text-sm leading-relaxed text-zinc-300 min-h-[3rem]">
            {transcriptWindow.map((w, i) => (
              <motion.span
                key={`${transcriptIdx}-${i}`}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: i === transcriptWindow.length - 1 ? 0.4 : 1, y: 0 }}
                transition={{ duration: 0.25 }}
                className="inline-block mr-1.5"
                style={{ color: w === w.toUpperCase() && w.length > 2 ? '#D4FF00' : '#e4e4e7' }}
              >
                {w}
              </motion.span>
            ))}
            <motion.span animate={{ opacity: [0.2, 1, 0.2] }} transition={{ duration: 0.8, repeat: Infinity }} className="inline-block w-1.5 h-4 bg-[#D4FF00] align-middle" />
          </div>
        </div>
      </div>

      {/* Highlight detection ticker */}
      <div className="p-5 rounded-2xl border border-white/5 bg-white/[0.02]" data-testid="processing-highlights">
        <div className="flex items-center justify-between mb-3">
          <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">// Highlights detected</span>
          <Brain className="w-4 h-4 text-[#A855F7]" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {FAKE_HIGHLIGHTS.map((h, i) => {
            const visible = i <= highlightIdx;
            return (
              <motion.div
                key={h.label}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: visible ? 1 : 0.15, x: 0 }}
                className="flex items-center justify-between px-3 py-2 rounded-lg bg-black/40 border border-white/5"
              >
                <span className="text-xs text-zinc-300">{h.label}</span>
                <span className="font-mono text-xs font-bold text-[#D4FF00]">{h.score}</span>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default UploadProcessing;
